'use client';

import { getEventId } from '@/utils/get-event-id';
import {
  getCurrencyInfo,
  getDefaultCurrencyCode,
} from '@hidayahlab/academy-suite-corelib';
import { useCallback } from 'react';
import {
  FacebookPixelEventType,
  useFacebookPixelTrack,
} from './use-facebook-pixel-api';

const fbqTrack = (
  event: string,
  data: Record<string, any> = {},
  eventId?: string,
) => {
  if (
    typeof window !== 'undefined' &&
    typeof (window as any).fbq === 'function'
  ) {
    try {
      if (eventId) {
        (window as any).fbq('track', event, data, { eventID: eventId });
      } else {
        (window as any).fbq('track', event, data);
      }
    } catch (err) {
      console.warn('FBQ tracking error:', err);
    }
  }
};

export function useFacebookPixelEvents() {
  const { trigger, isMutating, error, customer } = useFacebookPixelTrack();

  const currencyInfo = getCurrencyInfo(
    getDefaultCurrencyCode(customer?.currencies),
  );

  const trackPageView = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();

      fbqTrack('PageView', custom_data, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'PageView',
        event_id: eventId,
        user_data,
        custom_data,
      });
    },
    [trigger, customer],
  );

  const trackPurchase = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();
      const mergedData = { ...custom_data, currency: currencyInfo.code };

      fbqTrack('Purchase', mergedData, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'Purchase',
        event_id: eventId,
        user_data,
        custom_data: mergedData,
      });
    },
    [trigger, currencyInfo, customer],
  );

  const trackAddToCart = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();
      const mergedData = { ...custom_data, currency: currencyInfo.code };

      fbqTrack('AddToCart', mergedData, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'AddToCart',
        event_id: eventId,
        user_data,
        custom_data: mergedData,
      });
    },
    [trigger, currencyInfo, customer],
  );

  const trackViewContent = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();

      fbqTrack('ViewContent', custom_data, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'ViewContent',
        event_id: eventId,
        user_data,
        custom_data,
      });
    },
    [trigger, customer],
  );

  const trackInitiateCheckout = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();
      const mergedData = { ...custom_data, currency: currencyInfo.code };

      fbqTrack('InitiateCheckout', mergedData, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'InitiateCheckout',
        event_id: eventId,
        user_data,
        custom_data: mergedData,
      });
    },
    [trigger, currencyInfo, customer],
  );

  const trackCompleteRegistration = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();

      fbqTrack('CompleteRegistration', custom_data, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'CompleteRegistration',
        event_id: eventId,
        user_data,
        custom_data,
      });
    },
    [trigger, customer],
  );

  const trackWebinarRegistration = useCallback(
    (
      user_data: FacebookPixelEventType['user_data'] = {},
      custom_data: FacebookPixelEventType['custom_data'] = {},
    ) => {
      if (!customer?.isFacebookPixelConnected) return Promise.resolve(null);

      const eventId = getEventId();

      fbqTrack('webinar_registration', custom_data, eventId);

      return trigger({
        action_source: 'website',
        event_name: 'webinar_registration',
        event_id: eventId,
        user_data,
        custom_data,
      });
    },
    [trigger, customer],
  );

  return {
    trackPageView,
    trackPurchase,
    trackAddToCart,
    trackViewContent,
    trackInitiateCheckout,
    trackCompleteRegistration,
    trackWebinarRegistration,
    isLoading: isMutating,
    error,
  };
}
